"use client";

import { useEffect, useRef } from "react";

const AGENTS = [
  {
    id: "ir",
    name: "IR Agent",
    subtitle: "Investor Relations",
    status: "live" as const,
    description: "Analyze earnings calls, SEC filings, guidance revisions, and analyst consensus data. Ask about revenue drivers, margin trends, or guidance changes and get cited answers from your uploaded documents.",
    capabilities: [
      "Earnings call Q&A with timestamp citations",
      "10-K/10-Q financial data extraction",
      "Guidance revision tracking",
      "Peer benchmarking from uploaded comps",
    ],
    docTypes: ["Earnings Transcripts", "10-K / 10-Q", "Press Releases", "Analyst Reports"],
    color: "#0FF4C6",
    bgGradient: "linear-gradient(135deg, rgba(15,244,198,0.08), rgba(15,244,198,0.02))",
    borderColor: "rgba(15,244,198,0.2)",
    colSpan: "lg:col-span-2",
    rowSpan: "",
  },
  {
    id: "regulatory",
    name: "Regulatory Agent",
    subtitle: "FDA & EMA Intelligence",
    status: "soon" as const,
    description: "Navigate FDA guidance documents, EMA scientific opinions, and approval letters with precision.",
    capabilities: [
      "FDA Complete Response Letter analysis",
      "Regulatory pathway comparison",
    ],
    docTypes: ["FDA Guidance", "EMA Opinions", "Advisory Committee"],
    color: "#818CF8",
    bgGradient: "linear-gradient(135deg, rgba(99,102,241,0.06), rgba(99,102,241,0.02))",
    borderColor: "rgba(99,102,241,0.15)",
    colSpan: "lg:col-span-1",
    rowSpan: "",
  },
  {
    id: "competitive",
    name: "Competitive Intelligence",
    subtitle: "Market & Pipeline Tracking",
    status: "soon" as const,
    description: "Track competitor pipeline milestones, market share shifts, and clinical trial outcomes across your therapeutic area.",
    capabilities: [
      "Pipeline milestone monitoring",
      "Market share trend analysis",
    ],
    docTypes: ["Clinical Trial Reports", "Conference Presentations"],
    color: "#F5A623",
    bgGradient: "linear-gradient(135deg, rgba(245,166,35,0.06), rgba(245,166,35,0.02))",
    borderColor: "rgba(245,166,35,0.15)",
    colSpan: "lg:col-span-1",
    rowSpan: "",
  },
  {
    id: "drug-discovery",
    name: "Drug Discovery Agent",
    subtitle: "Clinical & Scientific Data",
    status: "soon" as const,
    description: "Synthesize findings across clinical trial datasets, mechanism-of-action literature, and safety signal reports.",
    capabilities: [
      "Phase I–III efficacy extraction",
      "Safety signal cross-referencing",
    ],
    docTypes: ["CSRs", "Protocol Amendments", "Safety Reports"],
    color: "#F43F5E",
    bgGradient: "linear-gradient(135deg, rgba(244,63,94,0.06), rgba(244,63,94,0.02))",
    borderColor: "rgba(244,63,94,0.15)",
    colSpan: "lg:col-span-1",
    rowSpan: "",
  },
];

export default function AgentsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pad relative overflow-hidden"
      id="agents"
      style={{ background: "#0B0F1A" }}
    >
      <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 70% 60% at 50% 100%, rgba(99,102,241,0.06), transparent)" }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label justify-center">Agent Roster</span>
          <h2
            className="font-display text-4xl lg:text-5xl font-medium text-white leading-tight"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Specialized agents for every
            <br />
            <span className="gradient-text-teal">pharma research domain</span>
          </h2>
          <p className="mt-4 text-[#8892A4] text-lg max-w-2xl mx-auto">
            Each agent is trained with domain-specific retrieval strategies and prompt engineering. Admins control which agents each user can access.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* IR Agent — featured large card */}
          <div
            className="lg:col-span-2 bento-card p-7 lg:p-8 reveal-left"
            style={{
              background: AGENTS[0].bgGradient,
              borderColor: AGENTS[0].borderColor,
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: `${AGENTS[0].color}15`, border: `1px solid ${AGENTS[0].color}25` }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={AGENTS[0].color} strokeWidth="1.8">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-white" style={{ fontFamily: "Fraunces, serif" }}>
                      {AGENTS[0].name}
                    </h3>
                    <p className="text-xs text-[#4B5568]">{AGENTS[0].subtitle}</p>
                  </div>
                </div>
              </div>
              <div className="agent-badge-live flex-shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0FF4C6] animate-teal-pulse inline-block" />
                Live Now
              </div>
            </div>

            <p className="text-[#8892A4] text-sm leading-relaxed mb-6">
              {AGENTS[0].description}
            </p>

            {/* Capabilities */}
            <div className="grid sm:grid-cols-2 gap-2 mb-6">
              {AGENTS[0].capabilities.map((cap) => (
                <div key={cap} className="flex items-start gap-2">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0FF4C6" strokeWidth="2.5" className="mt-0.5 flex-shrink-0">
                    <path d="M9 12l2 2 4-4" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                  <span className="text-xs text-[#8892A4]">{cap}</span>
                </div>
              ))}
            </div>

            {/* Doc types */}
            <div className="flex flex-wrap gap-2">
              {AGENTS[0].docTypes.map((doc) => (
                <span
                  key={doc}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-medium text-[#8892A4]"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  {doc}
                </span>
              ))}
            </div>
          </div>

          {/* Regulatory Agent */}
          <div
            className="bento-card p-6 reveal-right"
            style={{
              background: AGENTS[1].bgGradient,
              borderColor: AGENTS[1].borderColor,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${AGENTS[1].color}15`, border: `1px solid ${AGENTS[1].color}25` }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={AGENTS[1].color} strokeWidth="1.8">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <div className="agent-badge-soon">Coming Q3 2026</div>
            </div>
            <h3 className="font-display text-lg font-semibold text-white mb-1" style={{ fontFamily: "Fraunces, serif" }}>
              {AGENTS[1].name}
            </h3>
            <p className="text-xs text-[#4B5568] mb-3">{AGENTS[1].subtitle}</p>
            <p className="text-[#8892A4] text-xs leading-relaxed mb-4">{AGENTS[1].description}</p>
            <div className="space-y-1.5">
              {AGENTS[1].capabilities.map((cap) => (
                <div key={cap} className="flex items-center gap-2">
                  <div className="w-1 h-1 rounded-full" style={{ background: AGENTS[1].color }} />
                  <span className="text-xs text-[#4B5568]">{cap}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Competitive Intelligence */}
          <div
            className="bento-card p-6 reveal"
            style={{
              background: AGENTS[2].bgGradient,
              borderColor: AGENTS[2].borderColor,
              transitionDelay: "100ms",
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${AGENTS[2].color}15`, border: `1px solid ${AGENTS[2].color}25` }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={AGENTS[2].color} strokeWidth="1.8">
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 00-3-3.87" />
                  <path d="M16 3.13a4 4 0 010 7.75" />
                </svg>
              </div>
              <div className="agent-badge-soon">Coming Q4 2026</div>
            </div>
            <h3 className="font-display text-lg font-semibold text-white mb-1" style={{ fontFamily: "Fraunces, serif" }}>
              {AGENTS[2].name}
            </h3>
            <p className="text-xs text-[#4B5568] mb-3">{AGENTS[2].subtitle}</p>
            <p className="text-[#8892A4] text-xs leading-relaxed">{AGENTS[2].description}</p>
          </div>

          {/* Drug Discovery Agent */}
          <div
            className="bento-card p-6 reveal"
            style={{
              background: AGENTS[3].bgGradient,
              borderColor: AGENTS[3].borderColor,
              transitionDelay: "200ms",
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: `${AGENTS[3].color}15`, border: `1px solid ${AGENTS[3].color}25` }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={AGENTS[3].color} strokeWidth="1.8">
                  <path d="M10 2v7.31" />
                  <path d="M14 9.3V1.99" />
                  <path d="M8.5 2h7" />
                  <path d="M14 9.3a6.5 6.5 0 11-4 0" />
                </svg>
              </div>
              <div className="agent-badge-soon">Coming 2027</div>
            </div>
            <h3 className="font-display text-lg font-semibold text-white mb-1" style={{ fontFamily: "Fraunces, serif" }}>
              {AGENTS[3].name}
            </h3>
            <p className="text-xs text-[#4B5568] mb-3">{AGENTS[3].subtitle}</p>
            <p className="text-[#8892A4] text-xs leading-relaxed">{AGENTS[3].description}</p>
          </div>

          {/* Admin control card */}
          <div
            className="bento-card p-6 reveal"
            style={{
              background: "linear-gradient(135deg, rgba(17,24,39,0.9), rgba(11,15,26,0.9))",
              transitionDelay: "300ms",
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
              style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8892A4" strokeWidth="1.8">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <h3 className="font-display text-base font-semibold text-white mb-2" style={{ fontFamily: "Fraunces, serif" }}>
              Admin Access Control
            </h3>
            <p className="text-[#4B5568] text-xs leading-relaxed mb-4">
              Control exactly which agents each user or team can access. Roll out capabilities selectively as your organization's needs grow.
            </p>
            <div className="space-y-2">
              {[
                { user: "IR Team", access: ["IR Agent"] },
                { user: "Regulatory Affairs", access: ["IR Agent", "Regulatory"] },
              ].map((row) => (
                <div key={row.user} className="flex items-center justify-between">
                  <span className="text-[11px] text-[#4B5568]">{row.user}</span>
                  <div className="flex gap-1">
                    {row.access.map((a) => (
                      <span
                        key={a}
                        className="px-1.5 py-0.5 rounded text-[9px] font-medium text-[#0FF4C6]"
                        style={{ background: "rgba(15,244,198,0.08)", border: "1px solid rgba(15,244,198,0.15)" }}
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}